import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import {
  FiUsers,
  FiEdit,
  FiTrash2,
  FiSearch,
  FiLock,
  FiUnlock,
  FiX,
  FiTrendingUp,
} from "react-icons/fi";
import { useToast } from "@chakra-ui/react";
import UserDetailsModal from "../components/UserDetailsModal";
import ConfirmModal from "../components/ConformModel/ConfirmModal";
import { getUserRole } from "../components/utilities/getUserRole";

const API_URL = "/api/v1/admin/users";

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [coinsModalOpen, setCoinsModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [profileFilter, setProfileFilter] = useState("all");
  const [genderFilter, setGenderFilter] = useState("all");
  const [blockFilter, setBlockFilter] = useState("all");

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPrevPage, setHasPrevPage] = useState(false);

  const [confirmAction, setConfirmAction] = useState(null);
  const [actionTargetUser, setActionTargetUser] = useState(null);

  const [form, setForm] = useState({
    fullName: "",
    email: "",
    phoneNumber: "",
    gender: "",
    crown: "",
    adminNotes: "",
    isActive: true,
    overallPhotoVerificationStatus: "pending",
    role: "user",
  });

  const [coinsForm, setCoinsForm] = useState({
    coins: "",
    reason: "Admin Credit",
  });

  const [saving, setSaving] = useState(false);
  const toast = useToast();
  const currentUserRole = getUserRole().role;
  const isSuperAdmin = currentUserRole === "super-admin";

  // Debounce search — wait 400ms after the user stops typing before fetching
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
  const searchDebounceRef = useRef(null);
  const handleSearchChange = (value) => {
    setSearchQuery(value);
    setCurrentPage(1);
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => setDebouncedSearch(value), 400);
  };

  // Fetch users with filters
  const fetchUsers = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const params = {
          page,
          limit: 20,
          sortBy: "createdAt",
          sortOrder: "desc",
        };

        if (debouncedSearch) params.search = debouncedSearch;
        if (statusFilter !== "all") params.isActive = statusFilter === "active";
        if (profileFilter !== "all")
          params.isProfileCompleted = profileFilter === "completed";
          params.gender = genderFilter;
        if (blockFilter !== "all") params.isBlocked = blockFilter === "blocked";

        const res = await axios.get(API_URL, { params });

        if (res.data.success) {
          setUsers(res.data.data || []);
          if (res.data.pagination) {
            setCurrentPage(res.data.pagination.page);
            setTotalPages(res.data.pagination.totalPages);
            setTotalUsers(res.data.pagination.totalUsers);
            setHasNextPage(res.data.pagination.hasNextPage);
            setHasPrevPage(res.data.pagination.hasPrevPage);
          }
        }
      } catch (error) {
        console.error("Error fetching users:", error);
        toast({
          title: "Error loading users",
          description: error.response?.data?.message || "Failed to fetch users",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    },
    [debouncedSearch, statusFilter, profileFilter, genderFilter, blockFilter, toast]
  );

  useEffect(() => {
    fetchUsers(currentPage);
  }, [currentPage, fetchUsers]);

  // Open edit modal
  const openEditModal = (user) => {
    setSelectedUser(user);
    setForm({
      fullName: user.fullName || "",
      email: user.email || "",
      phoneNumber: user.phoneNumber || "",
      gender: user.gender || "",
      crown: user.crown || "",
      adminNotes: user.adminNotes || "",
      isActive: user.isActive ?? true,
      overallPhotoVerificationStatus: user.overallPhotoVerificationStatus || "pending",
      role: user.role || "user",
    });
    setEditModalOpen(true);
  };

  // Open view modal — map API fields to what UserDetailsModal expects
  const openViewModal = (user) => {
    setSelectedUser(user);
    setViewModalOpen(true);
  };

  // Build the model prop that UserDetailsModal consumes
  const buildUserModel = (user) => {
    if (!user) return null;
    const dob = user.dateOfBirth
      ? new Date(user.dateOfBirth).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        })
      : "-";
    return {
      // identity
      name: user.fullName || "-",
      email: user.email || "-",
      phone: user.phoneNumber || "-",
      image: user.profileImage || "",
      photos: user.photos || [],
      gender: user.gender || "-",
      age: calculateAge(user.dateOfBirth),
      dob,
      role: user.role || "user",
      crown: user.crown || null,
      occupation: user.occupation || "-",
      aboutMe: user.aboutMe || "",
      interests: user.interests || [],
      address: user.location || "-",
      latitude: user.latitude || "-",
      longitude: user.longitude || "-",
      // activity
      isOnline: user.isOnline || false,
      isActive: user.isActive,
      isBlocked: user.isBlocked,
      isDeleted: user.isDeleted,
      lastActive: user.lastActive
        ? new Date(user.lastActive).toLocaleString()
        : "-",
      accountCreated: user.createdAt
        ? new Date(user.createdAt).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          })
        : "-",
      // social
      followers: user.followersCount ?? user.followers ?? 0,
      following: user.followingCount ?? user.following ?? 0,
      friends: user.friendsCount ?? user.friends ?? 0,
      // verification
      overallPhotoVerificationStatus: user.overallPhotoVerificationStatus || "pending",
      isVerifiedProfile: user.isVerifiedProfile || false,
      // subscription
      hasActiveSubscription: user.hasActiveSubscription || false,
      // referral
      referralCode: user.referralCode || "-",
      referredBy: user.referredBy || "-",
      // admin
      adminNotes: user.adminNotes || "",
    };
  };

  // Open coins modal
  const openCoinsModal = (user) => {
    setSelectedUser(user);
    setCoinsForm({ coins: "", reason: "Admin Credit" });
    setCoinsModalOpen(true);
  };

  // Save user changes
  const handleSaveUser = async () => {
    if (!form.fullName.trim()) {
      toast({
        title: "Validation Error",
        description: "Full name is required",
        status: "warning",
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    setSaving(true);
    try {
      const res = await axios.put(`${API_URL}/${selectedUser._id}`, form);
      toast({
        title: "Success",
        description: "User updated successfully!",
        status: "success",
        duration: 2500,
        isClosable: true,
      });
      await fetchUsers(currentPage);
      setEditModalOpen(false);
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to update user",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  // Add coins to user
  const handleAddCoins = async () => {
    if (!coinsForm.coins || Number(coinsForm.coins) <= 0) {
      toast({
        title: "Validation Error",
        description: "Please enter a valid amount",
        status: "warning",
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    setSaving(true);
    try {
      const res = await axios.post(`${API_URL}/add-coins-to-user`, {
        userId: selectedUser._id,
        coins: Number(coinsForm.coins),
        reason: coinsForm.reason,
      });
      toast({
        title: "Success",
        description: `${coinsForm.coins} coins added successfully!`,
        status: "success",
        duration: 2500,
        isClosable: true,
      });
      await fetchUsers(currentPage);
      setCoinsModalOpen(false);
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to add coins",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  // Block user
  const handleBlockUser = async (user) => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/block-user`, { userId: user._id });
      toast({
        title: "Success",
        description: "User blocked successfully!",
        status: "success",
        duration: 2500,
        isClosable: true,
      });
      await fetchUsers(currentPage);
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to block user",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
      setConfirmModalOpen(false);
      setConfirmAction(null);
      setActionTargetUser(null);
    }
  };

  // Unblock user
  const handleUnblockUser = async (user) => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/un-block-user`, { userId: user._id });
      toast({
        title: "Success",
        description: "User unblocked successfully!",
        status: "success",
        duration: 2500,
        isClosable: true,
      });
      await fetchUsers(currentPage);
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to unblock user",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
      setConfirmModalOpen(false);
      setConfirmAction(null);
      setActionTargetUser(null);
    }
  };

  // Delete user (soft delete)
  const handleDeleteUser = async (user) => {
    setSaving(true);
    try {
      await axios.delete(`${API_URL}/${user._id}`);
      toast({
        title: "Success",
        description: "User deleted successfully!",
        status: "success",
        duration: 2500,
        isClosable: true,
      });
      await fetchUsers(currentPage);
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to delete user",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
      setConfirmModalOpen(false);
      setConfirmAction(null);
      setActionTargetUser(null);
    }
  };

  // Approve photo verification
  const handleApprovePhoto = async (user) => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/approve-photo`, {
        userId: user._id,
        isApproved: true,
      });
      toast({
        title: "Success",
        description: "Photo verification approved!",
        status: "success",
        duration: 2500,
        isClosable: true,
      });
      await fetchUsers(currentPage);
    } catch (error) {
      toast({
        title: "Error",
        description:
          error.response?.data?.message || "Failed to approve verification",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
      setConfirmModalOpen(false);
      setConfirmAction(null);
      setActionTargetUser(null);
    }
  };

  // Helper functions
  const calculateAge = (dob) => {
    if (!dob) return "-";
    const today = new Date();
    const birthDate = new Date(dob);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    )
      age--;
    return age;
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusBadge = (user) => {
    if (user.isDeleted) return "Deleted";
    if (user.isBlocked) return "Blocked";
    if (!user.isActive) return "Inactive";
    return "Active";
  };

  const getStatusColor = (user) => {
    if (user.isDeleted) return "bg-red-500/20 text-red-300 border-red-500/30";
    if (user.isBlocked) return "bg-orange-500/20 text-orange-300 border-orange-500/30";
    if (!user.isActive) return "bg-yellow-500/20 text-yellow-300 border-yellow-500/30";
    return "bg-green-500/20 text-green-300 border-green-500/30";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="relative flex items-center justify-center">
          <div className="w-14 h-14 rounded-full border-4 border-cyan-500/20 border-t-cyan-400 animate-spin" />
          <div className="absolute inset-0 rounded-full blur-md bg-cyan-400/25 animate-pulse" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 text-cyan-50 p-2 md:p-4">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 pb-2 border-b border-cyan-500/20">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-wide text-transparent bg-clip-text bg-gradient-to-r from-cyan-200 via-sky-300 to-white">
            User Management
          </h1>
          <p className="text-xs sm:text-sm text-cyan-200/60 mt-1">
            Manage user accounts, permissions, and activity
          </p>
        </div>

        <div className="flex items-center gap-3 bg-cyan-950/40 border border-cyan-500/20 rounded-xl px-4 py-2.5 backdrop-blur-md">
          <FiUsers className="text-cyan-400" size={18} />
          <div>
            <p className="text-xs uppercase font-bold text-cyan-300/70">
              Total Users
            </p>
            <p className="text-xl font-black text-cyan-300">{totalUsers}</p>
          </div>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3">
        <div className="relative">
          <FiSearch className="absolute left-3 top-3 text-cyan-400" size={16} />
          <input
            type="text"
            placeholder="Search by name, email, phone..."
            value={searchQuery}
            onChange={(e) => {
              handleSearchChange(e.target.value);
            }}
            className="w-full bg-cyan-950/70 border border-cyan-500/30 rounded-xl pl-9 pr-4 py-2.5 text-xs text-white placeholder-cyan-400/50 focus:outline-none focus:border-cyan-300 transition"
          />
        </div>

        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setCurrentPage(1);
          }}
          className="bg-cyan-950/70 border border-cyan-500/30 rounded-xl px-4 py-2.5 text-xs text-white focus:outline-none focus:border-cyan-300 transition"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>

        <select
          value={genderFilter}
          onChange={(e) => {
            setGenderFilter(e.target.value);
            setCurrentPage(1);
          }}
          className="bg-cyan-950/70 border border-cyan-500/30 rounded-xl px-4 py-2.5 text-xs text-white focus:outline-none focus:border-cyan-300 transition"
        >
          <option value="all">All Genders</option>
          <option value="male">Male</option>
          <option value="female">Female</option>
          <option value="other">Others</option>
          <option value="prefer_not_to_say">Prefer not to say</option>
        </select>
        <select
          value={profileFilter}
          onChange={(e) => {
            setProfileFilter(e.target.value);
            setCurrentPage(1);
          }}
          className="bg-cyan-950/70 border border-cyan-500/30 rounded-xl px-4 py-2.5 text-xs text-white focus:outline-none focus:border-cyan-300 transition"
        >
          <option value="all">All Profiles</option>
          <option value="completed">Completed</option>
          <option value="incomplete">Incomplete</option>
        </select>

        <select
          value={blockFilter}
          onChange={(e) => {
            setBlockFilter(e.target.value);
            setCurrentPage(1);
          }}
          className="bg-cyan-950/70 border border-cyan-500/30 rounded-xl px-4 py-2.5 text-xs text-white focus:outline-none focus:border-cyan-300 transition"
        >
          <option value="all">All Blocks</option>
          <option value="blocked">Blocked Users</option>
          <option value="unblocked">Unblocked Users</option>
        </select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="p-4 rounded-2xl bg-cyan-950/40 border border-cyan-500/20 backdrop-blur-xl">
          <p className="text-xs uppercase font-bold text-cyan-300/70">
            Page {currentPage} of {totalPages}
          </p>
          <p className="text-2xl font-black text-cyan-300 mt-1">{users.length} users</p>
        </div>
        <div className="p-4 rounded-2xl bg-cyan-950/40 border border-cyan-500/20 backdrop-blur-xl">
          <p className="text-xs uppercase font-bold text-teal-300/70">
            Total Users
          </p>
          <p className="text-2xl font-black text-teal-300 mt-1">{totalUsers}</p>
        </div>
        <div className="p-4 rounded-2xl bg-cyan-950/40 border border-cyan-500/20 backdrop-blur-xl">
          <p className="text-xs uppercase font-bold text-sky-300/70">
            Navigation
          </p>
          <div className="flex gap-2 mt-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={!hasPrevPage}
              className="px-3 py-1 rounded-lg bg-cyan-500/20 border border-cyan-400/30 text-xs font-semibold disabled:opacity-50 hover:brightness-110 transition"
            >
              ← Prev
            </button>
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={!hasNextPage}
              className="px-3 py-1 rounded-lg bg-cyan-500/20 border border-cyan-400/30 text-xs font-semibold disabled:opacity-50 hover:brightness-110 transition"
            >
              Next →
            </button>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="rounded-2xl overflow-hidden backdrop-blur-xl border border-cyan-500/20">
        {users.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-cyan-950/50 border border-cyan-400/20 flex items-center justify-center">
              <FiUsers className="text-2xl text-cyan-400" />
            </div>
            <p className="text-cyan-300 font-semibold">No users found</p>
            <p className="text-cyan-200/60 text-sm mt-1">
              Try adjusting your search or filters
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr
                  className="bg-gradient-to-r from-cyan-950/60 to-blue-950/60 border-b border-cyan-500/20 text-cyan-300 uppercase tracking-wider font-bold"
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(6, 24, 60, 0.75) 0%, rgba(3, 13, 36, 0.85) 100%)",
                    boxShadow: "inset 0 1px 1px rgba(255, 255, 255, 0.05)",
                  }}
                >
                  <th className="py-4 pl-6 pr-4">User</th>
                  <th className="py-4 px-4">Status</th>
                  <th className="py-4 px-4">Gender</th>
                  <th className="py-4 px-4">Age</th>
                  <th className="py-4 px-4">Verification</th>
                  <th className="py-4 px-4">Joined</th>
                  <th className="py-4 pr-6 pl-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-cyan-500/10">
                {users.map((user) => (
                  <tr
                    key={user._id}
                    className="hover:bg-cyan-500/[0.04] transition-colors"
                  >
                    <td className="py-4 pl-6 pr-4">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-xl flex-shrink-0 flex items-center justify-center font-bold text-xs bg-gradient-to-tr from-cyan-600 to-blue-500 text-white border border-cyan-300/30 shadow-[0_0_12px_rgba(6,182,212,0.25)]">
                          {user.fullName
                            ? user.fullName
                                .split(" ")
                                .map((n) => n[0])
                                .join("")
                                .slice(0, 2)
                                .toUpperCase()
                            : "?"}
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-cyan-50 truncate">
                            {user.fullName}
                          </p>
                          <p className="text-[11px] text-cyan-300/60 truncate">
                            {user.phoneNumber}
                          </p>
                        </div>
                      </div>
                    </td>

                    <td className="py-4 px-4">
                      <span
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold border ${getStatusColor(
                          user
                        )}`}
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-current" />
                        {getStatusBadge(user)}
                      </span>
                    </td>

                    <td className="py-4 px-4 text-cyan-200/80 capitalize">
                      {user.gender || "—"}
                    </td>

                    <td className="py-4 px-4 text-cyan-200/80">
                      {calculateAge(user.dateOfBirth)}
                    </td>

                    <td className="py-4 px-4">
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-[10px] font-bold border ${
                          user.overallPhotoVerificationStatus === "approved"
                            ? "bg-green-500/20 text-green-300 border-green-500/30"
                            : user.overallPhotoVerificationStatus === "rejected"
                            ? "bg-red-500/20 text-red-300 border-red-500/30"
                            : "bg-yellow-500/20 text-yellow-300 border-yellow-500/30"
                        }`}
                      >
                        {user.overallPhotoVerificationStatus || "pending"}
                      </span>
                    </td>

                    <td className="py-4 px-4 text-cyan-200/80 text-[11px]">
                      {formatDate(user.createdAt)}
                    </td>

                    <td className="py-4 pr-6 pl-4 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={() => openViewModal(user)}
                          className="p-1.5 rounded-lg text-cyan-300 bg-cyan-950/60 border border-cyan-400/30 hover:bg-cyan-900/60 transition"
                          title="View details"
                        >
                          <FiSearch size={14} />
                        </button>

                        <button
                          onClick={() => openEditModal(user)}
                          className="p-1.5 rounded-lg text-cyan-300 bg-cyan-950/60 border border-cyan-400/30 hover:bg-cyan-900/60 transition"
                          title="Edit user"
                        >
                          <FiEdit size={14} />
                        </button>

                        {/* <button
                          onClick={() => openCoinsModal(user)}
                          className="p-1.5 rounded-lg text-teal-300 bg-teal-950/40 border border-teal-500/30 hover:bg-teal-900/40 transition"
                          title="Add coins"
                        >
                          <FiTrendingUp size={14} />
                        </button> */}

                        {!user.isBlocked ? (
                          <button
                            onClick={() => {
                              setActionTargetUser(user);
                              setConfirmAction("block");
                              setConfirmModalOpen(true);
                            }}
                            className="p-1.5 rounded-lg text-orange-300 bg-orange-950/40 border border-orange-500/30 hover:bg-orange-900/40 transition"
                            title="Block user"
                          >
                            <FiLock size={14} />
                          </button>
                        ) : (
                          <button
                            onClick={() => {
                              setActionTargetUser(user);
                              setConfirmAction("unblock");
                              setConfirmModalOpen(true);
                            }}
                            className="p-1.5 rounded-lg text-green-300 bg-green-950/40 border border-green-500/30 hover:bg-green-900/40 transition"
                            title="Unblock user"
                          >
                            <FiUnlock size={14} />
                          </button>
                        )}

                        {isSuperAdmin && (
                          <button
                            onClick={() => {
                              setActionTargetUser(user);
                              setConfirmAction("delete");
                              setConfirmModalOpen(true);
                            }}
                            className="p-1.5 rounded-lg text-rose-300 bg-rose-950/40 border border-rose-500/30 hover:bg-rose-900/40 transition"
                            title="Delete user"
                          >
                            <FiTrash2 size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit User Modal */}
      {editModalOpen && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
          <div className="w-full max-w-2xl rounded-3xl overflow-hidden border border-cyan-400/30 bg-gradient-to-b from-[#061e47]/95 to-[#020b22]/98 p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-white">Edit User</h3>
              <button
                onClick={() => setEditModalOpen(false)}
                className="p-2 rounded-lg text-cyan-400 hover:bg-cyan-950/50 transition"
              >
                <FiX size={20} />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-cyan-200 mb-2 font-semibold text-xs">
                  Full Name
                </label>
                <input
                  type="text"
                  value={form.fullName}
                  onChange={(e) => setForm({ ...form, fullName: e.target.value })}
                  className="w-full bg-cyan-950/70 border border-cyan-500/30 rounded-xl px-4 py-2.5 text-white text-sm outline-none focus:border-cyan-300 transition"
                />
              </div>

              <div>
                <label className="block text-cyan-200 mb-2 font-semibold text-xs">
                  Email
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full bg-cyan-950/70 border border-cyan-500/30 rounded-xl px-4 py-2.5 text-white text-sm outline-none focus:border-cyan-300 transition"
                />
              </div>

              <div>
                <label className="block text-cyan-200 mb-2 font-semibold text-xs">
                  Phone
                </label>
                <input
                  type="text"
                  value={form.phoneNumber}
                  readOnly
                  className="w-full bg-cyan-950/50 border border-cyan-500/20 rounded-xl px-4 py-2.5 text-cyan-200/60 text-sm outline-none"
                />
              </div>

              <div>
                <label className="block text-cyan-200 mb-2 font-semibold text-xs">
                  Gender
                </label>
                <select
                  value={form.gender}
                  onChange={(e) => setForm({ ...form, gender: e.target.value })}
                  className="w-full bg-cyan-950/70 border border-cyan-500/30 rounded-xl px-4 py-2.5 text-white text-sm outline-none focus:border-cyan-300 transition"
                >
                  <option value="">Select Gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                  <option value="prefer_not_to_say">Prefer not to say</option>
                </select>
              </div>

              <div>
                <label className="block text-cyan-200 mb-2 font-semibold text-xs">
                  Crown Level
                </label>
                <select
                  value={form.crown}
                  onChange={(e) => setForm({ ...form, crown: e.target.value })}
                  className="w-full bg-cyan-950/70 border border-cyan-500/30 rounded-xl px-4 py-2.5 text-white text-sm outline-none focus:border-cyan-300 transition"
                >
                  <option value="">None</option>
                  <option value="bronze">Bronze</option>
                  <option value="silver">Silver</option>
                  <option value="gold">Gold</option>
                  <option value="platinum">Platinum</option>
                </select>
              </div>

              <div className="flex items-end">
                <label className="flex items-center gap-2 cursor-pointer text-cyan-200 h-full">
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                    className="rounded accent-cyan-400 w-4 h-4"
                  />
                  <span className="text-sm">Active</span>
                </label>
              </div>

              <div>
                <label className="block text-cyan-200 mb-2 font-semibold text-xs">
                  Photo Verification
                </label>
                <select
                  value={form.overallPhotoVerificationStatus}
                  onChange={(e) =>
                    setForm({ ...form, overallPhotoVerificationStatus: e.target.value })
                  }
                  className="w-full bg-cyan-950/70 border border-cyan-500/30 rounded-xl px-4 py-2.5 text-white text-sm outline-none focus:border-cyan-300 transition"
                >
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>

              {isSuperAdmin && (
                <div>
                  <label className="block text-cyan-200 mb-2 font-semibold text-xs">
                    Role
                  </label>
                  <select
                    value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                    className="w-full bg-cyan-950/70 border border-cyan-500/30 rounded-xl px-4 py-2.5 text-white text-sm outline-none focus:border-cyan-300 transition"
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                    <option value="super-admin">Super Admin</option>
                  </select>
                </div>
              )}

              <div className="sm:col-span-2">
                <label className="block text-cyan-200 mb-2 font-semibold text-xs">
                  Admin Notes
                </label>
                <textarea
                  value={form.adminNotes}
                  onChange={(e) =>
                    setForm({ ...form, adminNotes: e.target.value.slice(0, 2000) })
                  }
                  maxLength="2000"
                  rows="4"
                  className="w-full bg-cyan-950/70 border border-cyan-500/30 rounded-xl px-4 py-2.5 text-white text-sm outline-none focus:border-cyan-300 transition resize-none"
                  placeholder="Internal notes..."
                />
                <p className="text-[10px] text-cyan-200/50 mt-1">
                  {form.adminNotes.length}/2000
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-6 border-t border-cyan-500/20">
              <button
                onClick={() => setEditModalOpen(false)}
                className="px-4 py-2 rounded-xl text-xs font-semibold bg-cyan-950/60 text-cyan-200 border border-cyan-500/30 hover:bg-cyan-900/60 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveUser}
                disabled={saving}
                className="px-5 py-2 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-cyan-500 to-blue-600 shadow-lg hover:brightness-110 disabled:opacity-50 transition"
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Coins Modal */}
      {coinsModalOpen && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
          <div className="w-full max-w-md rounded-3xl overflow-hidden border border-cyan-400/30 bg-gradient-to-b from-[#061e47]/95 to-[#020b22]/98 p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">Add Coins</h3>
              <button
                onClick={() => setCoinsModalOpen(false)}
                className="p-2 rounded-lg text-cyan-400 hover:bg-cyan-950/50 transition"
              >
                <FiX size={20} />
              </button>
            </div>

            <div className="bg-cyan-950/40 border border-cyan-500/20 rounded-xl p-4 mb-6">
              <p className="text-xs text-cyan-200/60">User</p>
              <p className="text-lg font-bold text-cyan-300">{selectedUser.fullName}</p>
            </div>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-cyan-200 mb-2 font-semibold text-xs">
                  Amount (Coins)
                </label>
                <input
                  type="number"
                  min="1"
                  value={coinsForm.coins}
                  onChange={(e) =>
                    setCoinsForm({ ...coinsForm, coins: e.target.value })
                  }
                  className="w-full bg-cyan-950/70 border border-cyan-500/30 rounded-xl px-4 py-2.5 text-white text-sm outline-none focus:border-cyan-300 transition"
                  placeholder="500"
                />
              </div>

              <div>
                <label className="block text-cyan-200 mb-2 font-semibold text-xs">
                  Reason
                </label>
                <input
                  type="text"
                  value={coinsForm.reason}
                  onChange={(e) =>
                    setCoinsForm({ ...coinsForm, reason: e.target.value })
                  }
                  className="w-full bg-cyan-950/70 border border-cyan-500/30 rounded-xl px-4 py-2.5 text-white text-sm outline-none focus:border-cyan-300 transition"
                  placeholder="Admin Credit"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-6 border-t border-cyan-500/20">
              <button
                onClick={() => setCoinsModalOpen(false)}
                className="px-4 py-2 rounded-xl text-xs font-semibold bg-cyan-950/60 text-cyan-200 border border-cyan-500/30 hover:bg-cyan-900/60 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleAddCoins}
                disabled={saving}
                className="px-5 py-2 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-teal-500 to-green-600 shadow-lg hover:brightness-110 disabled:opacity-50 transition"
              >
                {saving ? "Adding..." : "Add Coins"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {confirmModalOpen && actionTargetUser && (
        <ConfirmModal
          isOpen={confirmModalOpen}
          tone={
            confirmAction === "delete"
              ? "danger"
              : confirmAction === "block"
              ? "warning"
              : "success"
          }
          title={
            confirmAction === "block"
              ? "Block User"
              : confirmAction === "unblock"
              ? "Unblock User"
              : "Delete User"
          }
          message={
            confirmAction === "block"
              ? `Are you sure you want to block ${actionTargetUser.fullName}?`
              : confirmAction === "unblock"
              ? `Are you sure you want to unblock ${actionTargetUser.fullName}?`
              : `Are you sure you want to delete ${actionTargetUser.fullName}? This action cannot be undone.`
          }
          block={
            confirmAction === "block"
              ? "Yes, Block"
              : confirmAction === "unblock"
              ? "Yes, Unblock"
              : "Yes, Delete"
          }
          onConfirm={() => {
            if (confirmAction === "block") {
              handleBlockUser(actionTargetUser);
            } else if (confirmAction === "unblock") {
              handleUnblockUser(actionTargetUser);
            } else if (confirmAction === "delete") {
              handleDeleteUser(actionTargetUser);
            }
          }}
          onCancel={() => {
            setConfirmModalOpen(false);
            setConfirmAction(null);
            setActionTargetUser(null);
          }}
        />
      )}

      {/* User Details Modal */}
      {viewModalOpen && selectedUser && (
        <UserDetailsModal
          model={buildUserModel(selectedUser)}
          userId={selectedUser._id}
          onClose={() => {
            setViewModalOpen(false);
            setSelectedUser(null);
          }}
        />
      )}
    </div>
  );
};

export default Users;
